import type { ReactNode } from 'react';
import type {
  RootLayoutSiteinfoFragment,
  PageFragment,
  OpeningHoursDay,
  Maybe,
  KeyValue,
  ExceptionalOpeningHoursDay,
} from '@business/gql/graphql';
import { cn } from '@ngg/storefront-utils';
import { Dictionary } from '@ts/dictionary';
import Link from '@ui/components/Link/Link';
import Accordion from '@ui/components/Product/components/Accordion';
import Button from '@ui/components/button/buttonUI';
import Icon from '@ui/components/icon/icon';
import {
  getIrregularOpeningHoursDisplayData,
  getOpeningHoursDisplayData,
} from '@utils/content/getOpeningHours';
import { getPhoneNumberForLink } from '@utils/content/getPhoneNumberForLink';
import createDictionary from '@utils/createDictionary';
import { ArrowIcon } from '@ngg/icons';
import config from '@utils/config';
import Paragraph from '@ui/components/Paragraph/Paragraph';

type Props = {
  siteinfo?: Maybe<RootLayoutSiteinfoFragment>;
  dictionary: Dictionary;
};

export default function FooterInfo(props: Props) {
  const { siteinfo, dictionary } = props;
  let columnContent: { title: string; body: ReactNode }[];

  const newsletterForm = siteinfo?.newsletter;
  const newsletterDictionary = createDictionary(
    newsletterForm?.dictionaryCollection?.items as Array<KeyValue>,
  );

  const newsletterContent = {
    title: dictionary.newCustomerFormHeading,
    body: (
      <>
        <Paragraph>{dictionary.newCustomerFooterInfo}</Paragraph>
        <Link href={newsletterDictionary.newCustomerPageLink} className="group">
          <Button
            fullWidth
            endIcon={
              <Icon
                name="rightArrowWithTail"
                color="black"
                size={16}
                className={cn(
                  'transition-transform duration-300 ease-in-out lg:group-hover:translate-x-1',
                )}
              />
            }
            className={cn(
              'min-h-10 justify-between border border-gray-600 px-3 py-2 text-sm font-bold text-black',
            )}>
            {dictionary.signUp}
          </Button>
        </Link>
      </>
    ),
  };

  columnContent =
    siteinfo?.footer?.columnsCollection?.items?.map((column) => {
      if (column?.__typename === 'Store') {
        const phoneNumberWithCountryCode = getPhoneNumberForLink(
          column?.phoneNumber,
        );
        const openingHours = getOpeningHoursDisplayData(
          column?.openingHoursCollection?.items as OpeningHoursDay[],
          dictionary.closed,
        );
        const irregularOpeningHours = column.exceptionalOpeningHoursCollection
          ?.items as ExceptionalOpeningHoursDay[] | undefined;
        const irregularDisplayData = getIrregularOpeningHoursDisplayData(
          irregularOpeningHours,
          dictionary,
        );
        return {
          title: column.title ?? '',
          body: (
            <div className="flex flex-col gap-3">
              {column.infoText && (
                <Paragraph className="">{column.infoText}</Paragraph>
              )}
              {column.address && column.postalCode && column.city && (
                <Paragraph className="flex flex-col">
                  <span>{column.address}</span>
                  <span>
                    {column.postalCode} {column.city}
                  </span>
                </Paragraph>
              )}
              {column.type !== 'Online' && column.phoneNumber && (
                <a
                  className="block hover:underline"
                  href={`tel:${phoneNumberWithCountryCode}`}>
                  {column?.phoneNumber}
                </a>
              )}
              {column.type !== 'Online' && column.email && (
                <a
                  className="block hover:underline"
                  href={`mailto:${column?.email}`}>
                  {column?.email}
                </a>
              )}
              {column.type === 'Online' &&
                column.storePageLinkText &&
                column.storePage?.slug && (
                  <Link href={column.storePage.slug} className="group">
                    <Button
                      fullWidth
                      endIcon={
                        <Icon
                          name="rightArrowWithTail"
                          color="black"
                          size={16}
                          className={cn(
                            'transition-transform duration-300 ease-in-out lg:group-hover:translate-x-1',
                          )}
                        />
                      }
                      className={cn(
                        'min-h-10 justify-between border border-gray-600 px-3 py-2 text-sm font-bold text-black',
                      )}>
                      {column.storePageLinkText}
                    </Button>
                  </Link>
                )}
              {openingHours?.length ? (
                <div>
                  {openingHours.map((item) => (
                    <Paragraph
                      key={
                        item?.text
                      }>{`${item?.text}: ${item?.time}`}</Paragraph>
                  ))}
                  {irregularDisplayData.map((i) => (
                    <Paragraph
                      key={i?.text}>{`${i?.text}: ${i?.time}`}</Paragraph>
                  ))}
                </div>
              ) : null}

              {column?.storePageLinkText && column?.storePage?.slug && (
                <Link
                  locale={config.locale}
                  className="group inline-flex items-center underline"
                  href={column.storePage.slug}>
                  {column.storePageLinkText}
                  <ArrowIcon className="text-base text-black opacity-0 transition-[opacity,transform] group-hover:translate-x-0.5 group-hover:opacity-100 supports-[hover]:opacity-0" />
                </Link>
              )}
            </div>
          ),
        };
      }

      if (column?.__typename === 'Navigation') {
        return {
          title: column.title ?? '',
          body: (
            <div className="flex flex-col gap-1">
              {column.sublinksCollection?.items?.map((link) => {
                return (
                  <Link
                    className="place-self-start lg:hover:underline"
                    key={link?.title}
                    href={(link?.internalLink as PageFragment)?.slug ?? ''}>
                    {link?.title}
                  </Link>
                );
              })}
            </div>
          ),
        };
      }

      return {
        title: '',
        body: <></>,
      };
    }) ?? [];

  columnContent.push(newsletterContent);

  if (!columnContent.length) return null;

  return (
    <>
      {/* Newsletter signup on mobile */}
      <div className="border-t px-4 py-6 text-sm lg:hidden">
        <div className="flex grow flex-col gap-3">
          <Paragraph className="font-bold">{newsletterContent.title}</Paragraph>
          {newsletterContent.body}
        </div>
      </div>

      <div
        className={cn(
          'hidden bg-beige px-4 py-6 text-sm not-last:divide-x',
          'auto-cols-fr grid-flow-col gap-x-4 lg:grid',
        )}>
        {columnContent?.map(({ title, body }) => (
          <div
            className="flex grow border-beige-3 pb-14 pt-1 not-first:pl-4"
            key={title}>
            <div className="flex grow flex-col gap-3">
              <Paragraph className="font-bold">{title}</Paragraph>
              {body}
            </div>
            <div className="shrink" />
          </div>
        ))}
      </div>

      <div className="bg-beige px-4 lg:hidden">
        {columnContent?.map(({ title, body }, index) => (
          <Accordion
            key={title}
            title={title}
            classNames={{
              wrapper: cn(
                'last:hidden',
                index === columnContent?.length - 2 && 'border-b-0',
              ),
              button: 'md:px-0 py-4 text-sm',
              panel: 'md:px-0 text-sm',
            }}>
            {body}
          </Accordion>
        ))}
      </div>
    </>
  );
}
